import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Calculator, Layers, Edit3, Search } from "lucide-react";
import { toast } from "sonner";
import { useMonth } from "@/context/MonthContext";
import { useAuth } from "@/context/AuthContext";

export default function ManagerExpenses() {
  const { month } = useMonth();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "partner";

  const [items, setItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ manager_id:"", category:"", amount:"", description:"", date:`${month}-01` });
  const [search, setSearch] = useState("");

  const load = async () => {
    const [me, mg, co] = await Promise.all([
      api.get("/manager-expenses", { params: { month } }),
      api.get("/managers"),
      api.get("/companies"),
    ]);
    setItems(me.data); setManagers(mg.data); setCompanies(co.data);
  };
  useEffect(() => { load(); }, [month]);
  useEffect(() => { if (!editId) setForm(f => ({ ...f, date: `${month}-01` })); }, [month, editId]);

  const resetForm = () => {
    setEditId(null);
    setForm({ manager_id:"", category:"", amount:"", description:"", date:`${month}-01` });
  };

  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      manager_id: e.manager_id || "",
      category: e.category || "",
      amount: String(e.amount ?? ""),
      description: e.description || "",
      date: e.date,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, manager_id: form.manager_id || null, amount: Number(form.amount) };
      if (editId) {
        await api.put(`/manager-expenses/${editId}`, payload);
        toast.success("Gider güncellendi");
      } else {
        await api.post("/manager-expenses", payload);
        toast.success("Ortak gider eklendi — 5 firmaya %20 paylaştırılacak");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Gider silinsin mi?")) return;
    await api.delete(`/manager-expenses/${id}`); toast.success("Silindi"); load();
  };

  const total = items.reduce((s,i)=>s + Number(i.amount), 0);
  const salaryTotal = managers.reduce((s,m)=>s + Number(m.salary || 0), 0);
  const grand = total + salaryTotal;
  const nCo = Math.max(companies.length, 1);
  const perCo = grand / nCo;
  const mgrName = (id) => managers.find(m=>m.id===id)?.name || "Genel";

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return items;
    return items.filter(e =>
      (e.category || "").toLocaleLowerCase("tr").includes(q) ||
      (e.description || "").toLocaleLowerCase("tr").includes(q) ||
      mgrName(e.manager_id).toLocaleLowerCase("tr").includes(q) ||
      String(e.amount).includes(q)
    );
  }, [items, search, managers]);
  const filteredTotal = filtered.reduce((s,i)=>s + Number(i.amount), 0);

  return (
    <div className="space-y-6" data-testid="mgr-exp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500">Ortak Giderler</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">Ortak Giderler · Otomatik Paylaşım</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Bu sayfada eklenen tüm giderler + ortak maaşları, ay sonunda {nCo} firmaya
            <span className="text-amber-300 font-medium"> eşit (%{(100/nCo).toFixed(2)}) </span> paylaştırılarak firma net kârından düşülür.
          </p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-mexp-btn" onClick={openCreate} className="bg-amber-600 hover:bg-amber-500">
                <Plus className="w-4 h-4 mr-1.5"/> Yeni Ortak Gider
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131b2e] border-white/10">
              <DialogHeader><DialogTitle>{editId ? "Ortak Gideri Düzenle" : "Yeni Ortak Gider"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tutar (₺)</Label>
                    <Input required type="number" step="0.01" data-testid="mexp-amount" value={form.amount}
                           onChange={(e)=>setForm({...form, amount:e.target.value})}
                           className="bg-slate-900/60 border-slate-700"/>
                  </div>
                  <div className="space-y-2">
                    <Label>Tarih</Label>
                    <Input required type="date" data-testid="mexp-date" value={form.date}
                           onChange={(e)=>setForm({...form, date:e.target.value})}
                           className="bg-slate-900/60 border-slate-700"/>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Input data-testid="mexp-desc" value={form.description}
                         onChange={(e)=>setForm({...form, description:e.target.value})}
                         className="bg-slate-900/60 border-slate-700"/>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="mexp-submit" className="bg-amber-600 hover:bg-amber-500">
                    {editId ? "Güncelle" : "Kaydet"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Ortak Maaş Toplamı</div>
          <div className="text-xl font-semibold fk-mono mt-2 text-amber-300">{formatTRY(salaryTotal)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Ortak Gider Toplamı</div>
          <div className="text-xl font-semibold fk-mono mt-2 text-amber-300">{formatTRY(total)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Dağıtılacak Toplam</div>
          <div className="text-xl font-semibold fk-mono mt-2 text-white">{formatTRY(grand)}</div>
        </div>
        <div className="fk-card p-5 border-indigo-500/25">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Firma Başına (%{(100/nCo).toFixed(2)})</div>
          <div className="text-xl font-semibold fk-mono mt-2 text-indigo-300">{formatTRY(perCo)}</div>
        </div>
      </div>

      <div className="fk-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-indigo-400"/>
          <div className="text-sm font-semibold">Firma Paylaşım Önizlemesi</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {companies.map(c => (
            <div key={c.id} className="rounded-lg p-3 bg-indigo-500/5 border border-indigo-500/15">
              <div className="text-xs text-slate-400 truncate">{c.name}</div>
              <div className="fk-mono text-sm font-semibold text-indigo-300 mt-1">{formatTRY(perCo)}</div>
              <div className="text-[10px] text-slate-500 fk-mono mt-0.5">%{(100/nCo).toFixed(2)}</div>
            </div>
          ))}
          {companies.length === 0 && <div className="text-sm text-slate-500 col-span-full">Firma bulunamadı.</div>}
        </div>
      </div>

      <div className="fk-card overflow-hidden">
        <div className="p-3 border-b border-white/5">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="mexp-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Ara: kategori, açıklama, tutar…"
                   className="h-9 pl-9 bg-slate-900/60 border-slate-700" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Tarih</th>
                <th className="text-left px-5 py-3 font-medium">Ortak</th>
                <th className="text-left px-5 py-3 font-medium">Kategori</th>
                <th className="text-left px-5 py-3 font-medium">Açıklama</th>
                <th className="text-right px-5 py-3 font-medium">Tutar</th>
                {canEdit && <th className="w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`mexp-row-${e.id}`} className="hover:bg-white/[.02]">
                  <td className="px-5 py-3 fk-mono text-slate-400">{e.date}</td>
                  <td className="px-5 py-3 text-slate-300">{mgrName(e.manager_id)}</td>
                  <td className="px-5 py-3">
                    <span className="fk-pill bg-amber-500/10 text-amber-300 border border-amber-500/25">
                      <Calculator className="w-3 h-3"/> {e.category}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-400 max-w-xs truncate">{e.description || "—"}</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300 font-medium">{formatTRY(e.amount)}</td>
                  {canEdit && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(e)}
                                data-testid={`edit-mexp-${e.id}`}
                                className="text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 h-8 w-8 p-0">
                          <Edit3 className="w-4 h-4"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>remove(e.id)}
                                data-testid={`del-mexp-${e.id}`}
                                className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4"/>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                  {search ? `"${search}" için gider bulunamadı` : "Bu ay için ortak gider yok."}
                </td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-white/[.03] font-semibold border-t border-white/10">
                <tr>
                  <td className="px-5 py-3 text-slate-300 uppercase text-xs tracking-wider" colSpan={4}>Toplam ({filtered.length})</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300" data-testid="mexp-total">{formatTRY(filteredTotal)}</td>
                  {canEdit && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
